import {useQueryClient} from '../react-query';
import {useCallback} from 'react';
import {
  InferConfigType,
  InferFinalResponseType,
  InferResponseType,
  MakeAllKeysRequired,
  MarkConfigOptionalForNonBulkRequests,
} from '../types';
import {createCacheKey} from './createCacheKey';
import {generateCacheKey} from '../useRequest';
import {GenericGripsConfig} from './useLegacyGrpcRequest';
import {GenericTadaridaConfig} from './useGrpcRequest';
import {
  GrpcRequestServiceMethod,
  GrpcQueryConfigWithSection,
} from './useGenericGrpcRequest';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';

/**
 * A convenience wrapper around `useQueryClient` to either invalidate a GRPC request so that it refetches.
 * Or update the cache with new data.
 * @param serviceMethod the GRPC service method to call
 * @param params the parameters for the specific GRPC function
 * @param grpcQueryConfig the config for useRequest react-query
 * @deprecated use the `updateCache` property of original useGrpcRequest or useLegacyGrpcRequest
 */
export function useUpdateGrpcRequestCache<
  ServiceMethod extends GrpcRequestServiceMethod,
  MethodName extends ServiceMethod['method'],
  ResponseType extends InferResponseType<ReturnType<ServiceMethod>>,
  ParamsType extends MakeAllKeysRequired<
    Omit<
      Parameters<ServiceMethod>[1],
      keyof GenericTadaridaConfig | keyof GenericGripsConfig
    >
  >,
  DefaultConfigType extends GrpcQueryConfigWithSection<ResponseType>,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(
  serviceMethod: ServiceMethod,
  params: ParamsType,
  ...[grpcQueryConfig]: MarkConfigOptionalForNonBulkRequests<
    MethodName,
    GrpcConfigType
  >
) {
  const cacheKeyParams = {
    uniqueIdentifier:
      grpcQueryConfig?.uniqueIdentifier ??
      (('method' in serviceMethod
        ? (serviceMethod as ServiceMethod).method
        : undefined) as string),
    params: convertAllNullPropertiesToUndefined<ParamsType>(params),
  };

  // there is a problem where if you add transformCacheKey, it would stop page from rerendering.
  // Example useBasketPageBulk.
  // pressumption is that grpc will have different caches so when updating it wont update right one.
  const key = createCacheKey(
    grpcQueryConfig?.transformCacheKey
      ? grpcQueryConfig.transformCacheKey(cacheKeyParams)
      : cacheKeyParams,
  );

  const queryClient = useQueryClient();

  return useCallback(
    (data: TData) => {
      if (grpcQueryConfig && 'section' in grpcQueryConfig) {
        queryClient.setQueryData<TData>(
          generateCacheKey(key),
          (currentQueryCache: TData | undefined) => {
            return currentQueryCache
              ? {
                  ...currentQueryCache,
                  [(grpcQueryConfig as unknown as DefaultConfigType).section]:
                    data,
                }
              : data;
          },
        );
      } else {
        queryClient.setQueryData<TData>(generateCacheKey(key), data);
      }
    },
    [
      (grpcQueryConfig as unknown as DefaultConfigType)?.section,
      key,
      queryClient,
    ],
  );
}
